'use client';

import { useMemo } from 'react';
import { ModelElement } from '../private/elements/model';
import { VideoElement } from '../private/elements/video';
import { VideoInfoElement } from '../private/elements/video-info';
import { ModelDisplay, QueryResponse, VideoDisplay } from '@/shared';
import { filterVideos, filterModels } from './actions/list';
import { useSearch } from '@/app/search-context';

export default function ListClient({
  className,
  elements: es,
  type,
  sizes,
}: {
  className: string,
  elements: QueryResponse<VideoDisplay | ModelDisplay>,
  type: string,
  sizes?: string,
}) {
  const {
    allVideos, allModels, dataLoading,
    pageQuery, videoSort, modelSort, selectedVideoTags, selectedModelTags, measurementFilters,
  } = useSearch();

  // With an active sort/tag/query, swap to client data only once everything
  // is loaded — partial data would show wrong items. Without filters,
  // server- and client-side orderings match, so append as batches arrive.
  const isFiltered = !!pageQuery || (
    (type === `video` || type === `video-info`)
      ? !!videoSort || selectedVideoTags.length > 0
      : !!modelSort || selectedModelTags.length > 0
  );

  const haveAnyData = (type === `video` || type === `video-info`)
    ? allVideos !== null && allVideos.length > 0
    : allModels !== null && allModels.length > 0;

  const useFilteredData = isFiltered ? !dataLoading : haveAnyData;

  const filteredData = useMemo(() => {
    if (!useFilteredData) return null;
    if (type === `video` || type === `video-info`)
      return filterVideos(allVideos!, pageQuery, videoSort, selectedVideoTags);
    return filterModels(allModels!, pageQuery, modelSort, selectedModelTags, measurementFilters);
  }, [useFilteredData, allVideos, allModels, pageQuery, videoSort, modelSort, selectedVideoTags, selectedModelTags, measurementFilters, type,]);

  const displayData = useFilteredData ? filteredData! : es.data;

  return (
    <>
      <div className={className}>
        {!displayData || !displayData.length ?
          <div className="list-info">No results found</div> :
          displayData.map((element) => {
            switch (type) {
            case `video`:
              return <VideoElement key={(element as VideoDisplay).slug} video={element as VideoDisplay} sizes={sizes} />;
            case `video-info`:
              return <VideoInfoElement key={(element as VideoDisplay).slug} video={element as VideoDisplay} sizes={sizes} />;
            case `model`:
              return <ModelElement key={(element as ModelDisplay).slug} model={element as ModelDisplay} sizes={sizes} />;
            default:
              throw new Error(`Unknown element component (${type})`);
            }
          })}
      </div>
      {dataLoading && <div className="list-info"><div className="animate-spin rounded-full h-6 w-6 border-4 border-t-transparent" /></div>}
    </>
  );
}
